import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { ReportViewDto } from '@ecom/contracts';
import type { MessageKey } from '@/i18n/messages';
import { useT } from '@/i18n/use-t';
import { useReportsStore, type ReportTab } from '@/stores/reports.store';
import { Button, Checkbox, Input, Pill, cx } from './ui';

/**
 * Saved views (ADR-119): the reports as someone left them — period rule,
 * filters, a custom report's columns — under a name, theirs or the team's.
 */
export function ReportViewsMenu({ tab, onOpen }: { tab: ReportTab; onOpen: (view: ReportViewDto) => void }) {
  const { views, loadViews, saveView, removeView, applyView } = useReportsStore();
  const t = useT();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void loadViews();
  }, [loadViews]);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (panel.current && !panel.current.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    const ok = await saveView({ name: name.trim(), report: tab, shared });
    setSaving(false);
    if (ok) {
      setName('');
      setShared(false);
    }
  }

  // This report's views first, then the rest — a view may open any report.
  const sorted = [...views].sort((a, b) => Number(b.report === tab) - Number(a.report === tab));

  return (
    <div ref={panel} className="relative">
      <Button variant="secondary" size="sm" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        {t('reportViews')}
        {views.length > 0 ? <span className="text-ink-faint">({views.length})</span> : null}
      </Button>
      {open ? (
        <div className="absolute end-0 z-30 mt-2 w-80 rounded-card border border-line bg-surface p-3 shadow-raised">
          {sorted.length === 0 ? <p className="px-1 pb-2 text-xs text-ink-muted">{t('reportNoViews')}</p> : null}
          <ul className="flex max-h-72 flex-col gap-1 overflow-y-auto">
            {sorted.map((view) => (
              <li key={view.id} className={cx('flex items-center gap-2 rounded-control px-2 py-1.5 hover:bg-surface-sunken', view.report === tab && 'bg-surface-sunken/60')}>
                <button
                  type="button"
                  className="min-w-0 flex-1 text-start"
                  onClick={() => {
                    applyView(view);
                    onOpen(view);
                    setOpen(false);
                  }}
                >
                  <span className="block truncate text-sm text-ink">{view.name}</span>
                  <span className="block truncate text-xs text-ink-muted">
                    {t(`reportTab_${view.report}` as MessageKey)}
                    {view.mine ? '' : ` · ${view.ownerName}`}
                  </span>
                </button>
                {view.shared ? <Pill tone="info">{t('reportShared')}</Pill> : null}
                {view.mine ? (
                  <button type="button" aria-label={t('remove')} className="px-1 text-ink-faint hover:text-danger" onClick={() => void removeView(view.id)}>
                    ×
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
          <form onSubmit={save} className="mt-3 flex flex-col gap-2 border-t border-line pt-3">
            <span className="text-xs font-medium text-ink">{t('reportSaveView')}</span>
            <Input value={name} onChange={(event) => setName(event.target.value)} placeholder={t('reportViewName')} maxLength={80} />
            <label className="flex items-center gap-2 text-xs text-ink-muted">
              <Checkbox label={t('reportShareView')} checked={shared} onChange={(event) => setShared(event.target.checked)} />
              {t('reportShareView')}
            </label>
            <Button type="submit" size="sm" disabled={saving || !name.trim()}>
              {t('save')}
            </Button>
          </form>
        </div>
      ) : null}
    </div>
  );
}
